import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Gamepad2,
  Grid,
  List,
  Monitor,
  PlayCircle,
  Search,
  Share2,
  Smartphone,
  Tag,
  Tv
} from 'lucide-react';
import { ProductService } from '../../services/productService';
import { CategoryService } from '../../services/categoryService';
import LoadingSpinner from '../common/LoadingSpinner';

const CARD_GRADIENTS = [
  'from-sky-600 via-blue-600 to-indigo-700',
  'from-emerald-500 via-teal-500 to-cyan-600',
  'from-violet-500 via-fuchsia-500 to-purple-600',
  'from-amber-500 via-orange-500 to-red-500',
  'from-slate-700 via-slate-800 to-slate-900',
  'from-cyan-500 via-blue-500 to-indigo-600',
  'from-purple-500 via-pink-500 to-rose-500',
  'from-green-500 via-emerald-500 to-lime-500'
];

const SERVICE_ICON_MAP = {
  jeux: Gamepad2,
  gaming: Gamepad2,
  abonnement: Tv,
  abonnements: Tv,
  streaming: Tv,
  mobile: Smartphone
};

const normalizeKey = (value = '') => CategoryService.normalizeCategory(String(value || '').trim());

const formatPrice = (amount) => {
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0
  }).format(amount).replace('DZD', 'DA');
};

const resolveTopupCoverImage = (product, fallbackImage = null) => {
  return product?.coverImageUrl || product?.imageUrl || fallbackImage || null;
};

const buildImageStateKey = (scope, id, imageUrl = '') => `${scope}:${id}:${imageUrl}`;

const buildServiceSubtitle = (service) => {
  if (service.minPriceLabel) {
    return `A partir de ${service.minPriceLabel}`;
  }

  if (service.offerCount > 0) {
    return `${service.offerCount} offre(s) disponible(s)`;
  }

  return 'Service Topup';
};

const buildServiceMeta = (service) => {
  const parts = [];

  if (service.offerCount > 0) {
    parts.push(`${service.offerCount} offre${service.offerCount > 1 ? 's' : ''}`);
  }

  if (service.minPriceLabel) {
    parts.push(service.minPriceLabel);
  }

  return parts.join(' • ') || buildServiceSubtitle(service);
};

const getCategoryPresentation = (categoryName) => {
  const normalized = (categoryName || '').toLowerCase();

  if (normalized.includes('tv') || normalized.includes('stream')) {
    return {
      icon: PlayCircle,
      accentClass: 'text-cyan-500 bg-cyan-50',
      actionClass: 'text-cyan-500',
      cardClass: 'border-cyan-100 bg-gradient-to-br from-white via-cyan-50/70 to-sky-50/90',
      imageShellClass: 'from-cyan-50 via-white to-sky-50',
      orbClass: 'bg-cyan-200/60'
    };
  }

  if (normalized.includes('pc')) {
    return {
      icon: Monitor,
      accentClass: 'text-violet-500 bg-violet-50',
      actionClass: 'text-violet-500',
      cardClass: 'border-violet-100 bg-gradient-to-br from-white via-violet-50/70 to-fuchsia-50/90',
      imageShellClass: 'from-violet-50 via-white to-fuchsia-50',
      orbClass: 'bg-violet-200/60'
    };
  }

  if (
    normalized.includes('social') ||
    normalized.includes('reseau') ||
    normalized.includes('réseau')
  ) {
    return {
      icon: Share2,
      accentClass: 'text-emerald-500 bg-emerald-50',
      actionClass: 'text-emerald-500',
      cardClass: 'border-emerald-100 bg-gradient-to-br from-white via-emerald-50/70 to-green-50/90',
      imageShellClass: 'from-emerald-50 via-white to-green-50',
      orbClass: 'bg-emerald-200/60'
    };
  }

  if (normalized.includes('console')) {
    return {
      icon: Gamepad2,
      accentClass: 'text-blue-500 bg-blue-50',
      actionClass: 'text-blue-500',
      cardClass: 'border-blue-100 bg-gradient-to-br from-white via-blue-50/70 to-sky-50/90',
      imageShellClass: 'from-blue-50 via-white to-sky-50',
      orbClass: 'bg-blue-200/60'
    };
  }

  return {
    icon: Smartphone,
    accentClass: 'text-blue-500 bg-blue-50',
    actionClass: 'text-blue-500',
    cardClass: 'border-blue-100 bg-gradient-to-br from-white via-blue-50/70 to-sky-50/90',
    imageShellClass: 'from-blue-50 via-white to-sky-50',
    orbClass: 'bg-blue-200/60'
  };
};

const TopupHub = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  useEffect(() => {
    let mounted = true;

    const loadTopupData = async () => {
      try {
        setLoading(true);
        const [activeProducts, activeCategories] = await Promise.all([
          ProductService.getActiveProducts(),
          CategoryService.getActiveCategories()
        ]);

        if (!mounted) {
          return;
        }

        setProducts(activeProducts || []);
        setCategories(activeCategories || []);
      } catch (error) {
        console.error('Erreur lors du chargement du hub topup:', error);
        if (mounted) {
          setProducts([]);
          setCategories([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTopupData();

    return () => {
      mounted = false;
    };
  }, []);

  const topupParentCategories = useMemo(
    () => categories.filter((category) => CategoryService.isTopupParentCategory(category)),
    [categories]
  );

  const topupSubcategories = useMemo(
    () => categories.filter((category) => CategoryService.isTopupSubcategory(category)),
    [categories]
  );

  const services = useMemo(() => {
    const serviceMap = new Map();
    const topupCategoryMap = new Map();
    const topupSubcategoryMap = new Map();

    topupParentCategories.forEach((category) => {
      topupCategoryMap.set(category.id, category);
      topupCategoryMap.set(normalizeKey(category.name || category.id), category);
    });

    topupSubcategories.forEach((subcategory) => {
      topupSubcategoryMap.set(subcategory.id, subcategory);
      topupSubcategoryMap.set(
        `${subcategory.parentCategoryId}:${normalizeKey(subcategory.name || subcategory.id)}`,
        subcategory
      );
    });

    const resolveEligibleTopupCategory = (product = {}) => {
      const resolvedSection = ProductService.resolveProductSection(product, categories);
      if (resolvedSection !== CategoryService.CATEGORY_SECTIONS.TOPUP) {
        return null;
      }

      const categoryById = topupCategoryMap.get(product.categoryId) || null;
      const subcategoryById = topupSubcategoryMap.get(product.subcategoryId) || null;

      if (categoryById) {
        return categoryById;
      }

      if (subcategoryById) {
        return topupCategoryMap.get(subcategoryById.parentCategoryId) || null;
      }

      return topupCategoryMap.get(normalizeKey(product.category)) || null;
    };

    products.forEach((product) => {
      const topupCategory = resolveEligibleTopupCategory(product);

      if (!topupCategory) {
        return;
      }

      const productSubcategoryKey = `${topupCategory.id}:${normalizeKey(product.subcategoryName || '')}`;
      const topupSubcategory = topupSubcategoryMap.get(product.subcategoryId) ||
        topupSubcategoryMap.get(productSubcategoryKey) ||
        null;

      const serviceName = product.game || product.name || 'Service';
      const serviceKey = `${topupSubcategory?.id || `uncategorized:${topupCategory.id}`}:${normalizeKey(serviceName)}`;
      const currentEntry = serviceMap.get(serviceKey);
      const minOfferPrice = (product.offers || []).reduce((min, offer) => {
        const price = Number(offer?.price);
        if (!Number.isFinite(price) || price <= 0) {
          return min;
        }

        return Math.min(min, price);
      }, Number.POSITIVE_INFINITY);

      const nextEntry = currentEntry || {
        id: product.id,
        productId: product.id,
        name: serviceName,
        categoryId: topupCategory.id,
        parentCategoryId: topupCategory.id,
        parentCategoryLabel: topupCategory.name || product.category || 'Topup',
        categoryKey: normalizeKey(topupCategory.name || topupCategory.id),
        categoryLabel: topupCategory.name || product.category || 'Topup',
        subcategoryId: topupSubcategory?.id || '',
        subcategoryLabel: topupSubcategory?.name || '',
        imageUrl: resolveTopupCoverImage(product, topupCategory.imageUrl),
        href: `/topup/service/${product.id}`,
        offerCount: 0,
        minPrice: Number.POSITIVE_INFINITY,
        minPriceLabel: null,
        popular: false,
        freeFire: false,
        online: true
      };

      nextEntry.offerCount += Array.isArray(product.offers) ? product.offers.length : 0;
      nextEntry.minPrice = Math.min(nextEntry.minPrice, minOfferPrice);
      nextEntry.minPriceLabel = formatPrice(nextEntry.minPrice);

      if (!nextEntry.imageUrl) {
        nextEntry.imageUrl = resolveTopupCoverImage(product, topupCategory.imageUrl);
      }

      if (!nextEntry.productId) {
        nextEntry.productId = product.id;
        nextEntry.href = `/topup/service/${product.id}`;
      }

      serviceMap.set(serviceKey, nextEntry);
    });

    return Array.from(serviceMap.values()).sort((a, b) => {
      if (a.popular && !b.popular) return -1;
      if (!a.popular && b.popular) return 1;
      if ((b.offerCount || 0) !== (a.offerCount || 0)) {
        return (b.offerCount || 0) - (a.offerCount || 0);
      }

      return a.name.localeCompare(b.name);
    });
  }, [categories, products, topupParentCategories, topupSubcategories]);

  const categoryCards = useMemo(() => {
    return topupParentCategories
      .map((category) => {
        const categoryServices = services.filter((service) => service.parentCategoryId === category.id);

        return {
          ...category,
          serviceCount: categoryServices.length,
          serviceNames: categoryServices.map((service) => service.name),
          previewImageUrl: category.imageUrl || categoryServices.find((service) => service.imageUrl)?.imageUrl || null,
          minPriceLabel: formatPrice(
            categoryServices.reduce((min, service) => {
              const price = Number(service.minPrice);
              if (!Number.isFinite(price) || price <= 0) {
                return min;
              }

              return Math.min(min, price);
            }, Number.POSITIVE_INFINITY)
          )
        };
      })
      .filter((category) => category.serviceCount > 0)
      .sort((a, b) => b.serviceCount - a.serviceCount || a.name.localeCompare(b.name));
  }, [services, topupParentCategories]);

  const selectedCategory = useMemo(
    () => categoryCards.find((category) => category.id === selectedCategoryId) || null,
    [categoryCards, selectedCategoryId]
  );

  const subcategoryCards = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    const explicitSubcategories = topupSubcategories
      .filter((subcategory) => String(subcategory.parentCategoryId || '').trim() === selectedCategory.id)
      .map((subcategory) => {
        const subcategoryServices = services.filter((service) => (
          service.parentCategoryId === selectedCategory.id &&
          service.subcategoryId === subcategory.id
        ));

        return {
          ...subcategory,
          serviceCount: subcategoryServices.length,
          serviceNames: subcategoryServices.map((service) => service.name),
          previewImageUrl: subcategory.imageUrl || subcategoryServices.find((service) => service.imageUrl)?.imageUrl || selectedCategory.previewImageUrl || null,
          minPriceLabel: formatPrice(
            subcategoryServices.reduce((min, service) => {
              const price = Number(service.minPrice);
              if (!Number.isFinite(price) || price <= 0) {
                return min;
              }

              return Math.min(min, price);
            }, Number.POSITIVE_INFINITY)
          )
        };
      })
      .filter((subcategory) => subcategory.serviceCount > 0);

    const uncategorizedServices = services.filter((service) => (
      service.parentCategoryId === selectedCategory.id &&
      !service.subcategoryId
    ));

    if (uncategorizedServices.length > 0) {
      explicitSubcategories.push({
        id: `uncategorized:${selectedCategory.id}`,
        name: 'Autres services',
        section: CategoryService.CATEGORY_SECTIONS.TOPUP,
        type: CategoryService.CATEGORY_TYPES.SUBCATEGORY,
        parentCategoryId: selectedCategory.id,
        parentCategoryName: selectedCategory.name,
        serviceCount: uncategorizedServices.length,
        serviceNames: uncategorizedServices.map((service) => service.name),
        previewImageUrl: uncategorizedServices.find((service) => service.imageUrl)?.imageUrl || selectedCategory.previewImageUrl || null,
        minPriceLabel: formatPrice(
          uncategorizedServices.reduce((min, service) => {
            const price = Number(service.minPrice);
            if (!Number.isFinite(price) || price <= 0) {
              return min;
            }

            return Math.min(min, price);
          }, Number.POSITIVE_INFINITY)
        )
      });
    }

    return explicitSubcategories.sort((a, b) => b.serviceCount - a.serviceCount || a.name.localeCompare(b.name));
  }, [selectedCategory, services, topupSubcategories]);

  const selectedSubcategory = useMemo(
    () => subcategoryCards.find((subcategory) => subcategory.id === selectedSubcategoryId) || null,
    [selectedSubcategoryId, subcategoryCards]
  );

  useEffect(() => {
    const categoryIdFromUrl = searchParams.get('category') || '';
    const subcategoryIdFromUrl = searchParams.get('subcategory') || '';

    setSelectedCategoryId((current) => (
      current === categoryIdFromUrl ? current : categoryIdFromUrl
    ));
    setSelectedSubcategoryId((current) => (
      current === subcategoryIdFromUrl ? current : subcategoryIdFromUrl
    ));
  }, [searchParams]);

  const visibleCategories = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return categoryCards.filter((category) => {
      if (!normalizedSearch) {
        return true;
      }

      return (
        category.name.toLowerCase().includes(normalizedSearch) ||
        category.serviceNames.some((serviceName) => serviceName.toLowerCase().includes(normalizedSearch))
      );
    });
  }, [categoryCards, searchTerm]);

  const visibleServices = useMemo(() => {
    if (!selectedCategory || !selectedSubcategory) {
      return [];
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();

    return services.filter((service) => {
      if (service.parentCategoryId !== selectedCategory.id) {
        return false;
      }

      if (selectedSubcategory.id.startsWith('uncategorized:')) {
        if (service.subcategoryId) {
          return false;
        }
      } else if (service.subcategoryId !== selectedSubcategory.id) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return (
        service.name.toLowerCase().includes(normalizedSearch) ||
        buildServiceSubtitle(service).toLowerCase().includes(normalizedSearch)
      );
    });
  }, [searchTerm, selectedCategory, selectedSubcategory, services]);

  const visibleSubcategories = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();

    return subcategoryCards.filter((subcategory) => {
      if (!normalizedSearch) {
        return true;
      }

      return (
        subcategory.name.toLowerCase().includes(normalizedSearch) ||
        subcategory.serviceNames.some((serviceName) => serviceName.toLowerCase().includes(normalizedSearch))
      );
    });
  }, [searchTerm, selectedCategory, subcategoryCards]);

  const handleOpenService = (service) => {
    navigate(service.href);
  };

  const handleOpenCategory = (category) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('category', category.id);
    nextParams.delete('subcategory');
    setSearchParams(nextParams);
    setSearchTerm('');
  };

  const handleOpenSubcategory = (subcategory) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('category', selectedCategory.id);
    nextParams.set('subcategory', subcategory.id);
    setSearchParams(nextParams);
    setSearchTerm('');
  };

  const handleBackToCategories = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('category');
    nextParams.delete('subcategory');
    setSearchParams(nextParams);
    setSearchTerm('');
  };

  const handleBackToSubcategories = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('subcategory');
    setSearchParams(nextParams);
    setSearchTerm('');
  };

  const renderServiceVisual = (service, index) => {
    const Icon = SERVICE_ICON_MAP[service.categoryKey] || Gamepad2;
    const gradient = CARD_GRADIENTS[index % CARD_GRADIENTS.length];
    const serviceImageKey = buildImageStateKey('topup-service-card', service.id, service.imageUrl || '');

    if (service.imageUrl && !imageLoadErrors[serviceImageKey]) {
      return (
      <div className="rounded-[22px] border border-slate-100 bg-slate-50 p-2 shadow-sm">
        <div className="relative h-32 overflow-hidden rounded-[18px]">
          <img
            src={service.imageUrl}
            alt={service.name}
            className="h-full w-full object-cover"
            onError={() => setImageLoadErrors((current) => ({ ...current, [serviceImageKey]: true }))}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/20 to-transparent" />
          <div className="absolute left-3 top-3 rounded-full bg-white/90 p-2 text-slate-700 shadow-sm">
            <Icon className="h-4 w-4" />
          </div>
          {service.popular ? (
            <span className="absolute right-3 top-3 rounded-full bg-blue-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
              Populaire
            </span>
          ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className={`relative flex h-32 items-end overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} p-4 text-white`}>
        <div className="absolute left-3 top-3 rounded-full bg-white/15 p-2 backdrop-blur">
          <Icon className="h-4 w-4" />
        </div>
        {service.popular ? (
          <span className="absolute right-3 top-3 rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur">
            Populaire
          </span>
        ) : null}
        <div>
          <p className="text-2xl font-black tracking-tight">{service.name}</p>
          <p className="mt-1 text-xs text-white/80">{service.categoryLabel}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="md:min-h-screen md:bg-gray-50">
      <div className="md:mx-auto md:max-w-7xl md:px-4 md:py-4 lg:px-8 lg:py-8">
        <div className="space-y-6">
          <div className="md:hidden">
            <div className="flex items-center gap-3">
              {selectedCategory ? (
                <button
                  type="button"
                  onClick={selectedSubcategory ? handleBackToSubcategories : handleBackToCategories}
                  className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
                  aria-label="Retour aux categories"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              ) : null}

              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={selectedSubcategory ? 'Rechercher un service...' : selectedCategory ? 'Rechercher une sous-categorie...' : 'Rechercher une categorie...'}
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                />
              </div>
            </div>
          </div>

          <div className="hidden flex-col gap-4 rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm md:flex md:p-6">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
              <div className="flex flex-1 items-center gap-3">
                {selectedCategory ? (
                  <button
                    type="button"
                    onClick={selectedSubcategory ? handleBackToSubcategories : handleBackToCategories}
                    className="inline-flex h-12 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {selectedSubcategory ? 'Retour aux sous-categories' : 'Retour aux categories'}
                  </button>
                ) : null}
              
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder={selectedSubcategory ? 'Rechercher un service...' : selectedCategory ? 'Rechercher une sous-categorie...' : 'Rechercher une categorie...'}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50"
                  />
                </div>
              </div>

              <div className="inline-flex h-12 items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl transition ${
                    viewMode === 'grid'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                  aria-label="Afficher en grille"
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl transition ${
                    viewMode === 'list'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                  aria-label="Afficher en liste"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-0 md:rounded-[28px] md:border md:border-gray-200 md:bg-white md:p-5 md:shadow-sm lg:p-6">
            <div className="mb-5 hidden md:block">
              <h2 className="text-lg font-semibold text-slate-900">
                {selectedSubcategory ? 'Services de la sous-categorie' : selectedCategory ? 'Sous-categories Topup' : 'Categories Topup'}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {selectedSubcategory
                  ? `${visibleServices.length} service(s) visible(s) dans ${selectedSubcategory.name}`
                  : selectedCategory
                  ? `${visibleSubcategories.length} sous-categorie(s) visible(s) dans ${selectedCategory.name}`
                  : `${visibleCategories.length} categorie(s) disponible(s)`}
              </p>
            </div>

        {!selectedCategory ? (
          visibleCategories.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center">
              <p className="text-base font-semibold text-slate-700">Aucune categorie trouvee</p>
              <p className="mt-2 text-sm text-slate-500">
                Essayez un autre mot-cle pour retrouver une categorie topup.
              </p>
            </div>
          ) : (
            <>
              <div className={`md:hidden ${viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : 'space-y-4'}`}>
                {visibleCategories.map((category) => {
                  const presentation = getCategoryPresentation(category.name);
                  const AccentIcon = presentation.icon;
                  const mobileCategoryImageKey = buildImageStateKey('topup-mobile-category', category.id, category.previewImageUrl || '');

                  if (viewMode === 'grid') {
                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleOpenCategory(category)}
                        className={`group relative min-h-[198px] overflow-hidden rounded-[26px] border p-3 text-left shadow-[0_12px_30px_rgba(15,23,42,0.08)] transition active:scale-[0.99] ${presentation.cardClass}`}
                      >
                        <div className={`absolute -right-6 top-10 h-20 w-20 rounded-full blur-2xl ${presentation.orbClass}`} />
                        <div className={`absolute -bottom-6 -left-3 h-16 w-16 rounded-full blur-2xl ${presentation.orbClass}`} />

                        <div className="relative z-10 flex items-start gap-3">
                          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${presentation.accentClass}`}>
                            <AccentIcon className="h-5 w-5" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <h3 className="line-clamp-2 text-[1.05rem] font-semibold leading-5 text-slate-800">
                              {category.name}
                            </h3>
                          </div>
                        </div>

                        <div className={`relative z-10 mt-3 h-[122px] overflow-hidden rounded-[22px] bg-gradient-to-br ${presentation.imageShellClass}`}>
                          {category.previewImageUrl && !imageLoadErrors[mobileCategoryImageKey] ? (
                            <img
                              src={category.previewImageUrl}
                              alt={category.name}
                              className="h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
                              onError={() => setImageLoadErrors((current) => ({ ...current, [mobileCategoryImageKey]: true }))}
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center">
                              <Tag className="h-10 w-10 text-slate-300" />
                            </div>
                          )}

                          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white/60 to-transparent" />
                        </div>

                        <div className={`absolute bottom-4 right-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur ${presentation.actionClass}`}>
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleOpenCategory(category)}
                      className="overflow-hidden rounded-3xl border border-gray-200 bg-white text-left shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex min-h-[170px]">
                        <div className="flex w-[40%] min-w-0 flex-col justify-between p-4">
                          <div>
                            <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl ${presentation.accentClass}`}>
                              <AccentIcon className="h-5 w-5" />
                            </div>

                            <h3 className="text-xl font-semibold leading-tight text-gray-900">{category.name}</h3>
                            <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${presentation.actionClass}`}>
                              {category.serviceCount} service{category.serviceCount > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>

                        <div className="w-[60%] min-w-0 overflow-hidden">
                          {category.previewImageUrl && !imageLoadErrors[mobileCategoryImageKey] ? (
                            <img
                              src={category.previewImageUrl}
                              alt={category.name}
                              className="h-full w-full object-cover"
                              onError={() => setImageLoadErrors((current) => ({ ...current, [mobileCategoryImageKey]: true }))}
                            />
                          ) : (
                            <div className={`flex h-full items-center justify-center bg-gradient-to-br ${presentation.imageShellClass}`}>
                              <Tag className="h-12 w-12 text-slate-300" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
                        <span className={`text-sm font-semibold ${presentation.actionClass}`}>
                          Voir les sous-categories
                        </span>
                        <ArrowLeft className={`h-4 w-4 rotate-180 ${presentation.actionClass}`} />
                      </div>
                    </button>
                  );
                })}
              </div>

              {viewMode === 'grid' ? (
                <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-4">
                  {visibleCategories.map((category, index) => {
                    const categoryImageKey = buildImageStateKey('topup-category-grid', category.id, category.previewImageUrl || '');

                    return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleOpenCategory(category)}
                      className="group flex h-full flex-col rounded-[26px] border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg"
                    >
                      {category.previewImageUrl && !imageLoadErrors[categoryImageKey] ? (
                        <div className="rounded-[22px] border border-slate-100 bg-slate-50 p-2 shadow-sm">
                          <div className="relative h-36 overflow-hidden rounded-[18px]">
                            <img
                              src={category.previewImageUrl}
                              alt={category.name}
                              className="h-full w-full object-cover"
                              onError={() => setImageLoadErrors((current) => ({ ...current, [categoryImageKey]: true }))}
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/25 to-transparent" />
                          </div>
                        </div>
                      ) : (
                        <div className={`relative flex h-36 items-end overflow-hidden rounded-2xl bg-gradient-to-br ${CARD_GRADIENTS[index % CARD_GRADIENTS.length]} p-4 text-white`}>
                          <Tag className="absolute left-3 top-3 h-5 w-5 text-white/90" />
                          <p className="text-2xl font-black tracking-tight">{category.name}</p>
                        </div>
                      )}

                      <div className="flex flex-1 flex-col px-1 pb-1 pt-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-base font-semibold text-slate-900">{category.name}</h3>
                            <p className="mt-1 text-sm text-slate-500">
                              {category.minPriceLabel
                                ? `A partir de ${category.minPriceLabel}`
                                : `${category.serviceCount} service(s) disponible(s)`}
                            </p>
                          </div>
                          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition group-hover:border-blue-200 group-hover:text-blue-600">
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>

                        <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-400">
                          <span>{category.serviceCount} service(s)</span>
                          <span>Voir les sous-categories</span>
                        </div>
                      </div>
                    </button>
                    );
                  })}
                </div>
              ) : (
                <div className="hidden space-y-3 md:block">
                  {visibleCategories.map((category, index) => {
                    const categoryListImageKey = buildImageStateKey('topup-category-list', category.id, category.previewImageUrl || '');

                    return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleOpenCategory(category)}
                      className="group flex w-full items-center gap-4 rounded-[24px] border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-200 hover:shadow-md"
                    >
                      <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                        {category.previewImageUrl && !imageLoadErrors[categoryListImageKey] ? (
                          <img
                            src={category.previewImageUrl}
                            alt={category.name}
                            className="h-full w-full object-cover"
                            onError={() => setImageLoadErrors((current) => ({ ...current, [categoryListImageKey]: true }))}
                          />
                        ) : (
                          <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${CARD_GRADIENTS[index % CARD_GRADIENTS.length]} text-white`}>
                            <Tag className="h-5 w-5" />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-semibold text-slate-900">{category.name}</h3>
                        <p className="mt-1 truncate text-sm text-slate-500">
                          {category.minPriceLabel
                            ? `A partir de ${category.minPriceLabel}`
                            : `${category.serviceCount} service(s) disponible(s)`}
                        </p>
                      </div>

                      <span className="text-xs text-slate-400">{category.serviceCount} service(s)</span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-blue-600" />
                    </button>
                    );
                  })}
                </div>
              )}
            </>
          )
        ) : !selectedSubcategory ? (
          visibleSubcategories.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center">
              <p className="text-base font-semibold text-slate-700">Aucune sous-categorie trouvee</p>
              <p className="mt-2 text-sm text-slate-500">
                Cette categorie n a pas encore de sous-categorie ou aucun service n y est rattache.
              </p>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'space-y-4' : 'space-y-3'}>
              {viewMode === 'list' ? (
                <>
                  {visibleSubcategories.map((subcategory, index) => (
                    <button
                      key={subcategory.id}
                      type="button"
                      onClick={() => handleOpenSubcategory(subcategory)}
                      className="group flex w-full items-center gap-4 rounded-[24px] border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-200 hover:shadow-md"
                    >
                      <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                        {subcategory.previewImageUrl ? (
                          <img
                            src={subcategory.previewImageUrl}
                            alt={subcategory.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${CARD_GRADIENTS[index % CARD_GRADIENTS.length]} text-white`}>
                            <Tag className="h-5 w-5" />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-semibold text-slate-900">{subcategory.name}</h3>
                        <p className="mt-1 truncate text-sm text-slate-500">
                          {subcategory.minPriceLabel
                            ? `A partir de ${subcategory.minPriceLabel}`
                            : `${subcategory.serviceCount} service(s) disponible(s)`}
                        </p>
                      </div>

                      <span className="text-xs text-slate-400">{subcategory.serviceCount} service(s)</span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-blue-600" />
                    </button>
                  ))}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 md:hidden">
                    {visibleSubcategories.map((subcategory) => {
                      const presentation = getCategoryPresentation(selectedCategory?.name || subcategory.name);

                      return (
                        <button
                          key={subcategory.id}
                          type="button"
                          onClick={() => handleOpenSubcategory(subcategory)}
                          className={`group relative min-h-[190px] overflow-hidden rounded-[26px] border p-3 text-left shadow-[0_12px_30px_rgba(15,23,42,0.08)] transition active:scale-[0.99] ${presentation.cardClass}`}
                        >
                          <div className={`absolute -right-6 top-10 h-20 w-20 rounded-full blur-2xl ${presentation.orbClass}`} />
                          <div className={`absolute -bottom-6 -left-3 h-16 w-16 rounded-full blur-2xl ${presentation.orbClass}`} />
                          <div className="relative z-10 min-w-0">
                            <h3 className="line-clamp-2 text-[1.05rem] font-semibold leading-5 text-slate-800">
                              {subcategory.name}
                            </h3>
                            <p className="mt-2 text-xs text-slate-500">
                              {subcategory.minPriceLabel
                                ? `A partir de ${subcategory.minPriceLabel}`
                                : `${subcategory.serviceCount} service(s) disponible(s)`}
                            </p>
                          </div>

                          <div className={`relative z-10 mt-4 h-[108px] overflow-hidden rounded-[22px] bg-gradient-to-br ${presentation.imageShellClass}`}>
                            {subcategory.previewImageUrl ? (
                              <img
                                src={subcategory.previewImageUrl}
                                alt={subcategory.name}
                                className="h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center">
                                <Tag className="h-10 w-10 text-slate-300" />
                              </div>
                            )}
                          </div>

                          <div className={`absolute bottom-4 right-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur ${presentation.actionClass}`}>
                            <ArrowRight className="h-4 w-4" />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-4">
                    {visibleSubcategories.map((subcategory, index) => (
                      <button
                        key={subcategory.id}
                        type="button"
                        onClick={() => handleOpenSubcategory(subcategory)}
                        className="group flex h-full flex-col rounded-[26px] border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg"
                      >
                        {subcategory.previewImageUrl ? (
                          <div className="rounded-[22px] border border-slate-100 bg-slate-50 p-2 shadow-sm">
                            <div className="relative h-36 overflow-hidden rounded-[18px]">
                              <img
                                src={subcategory.previewImageUrl}
                                alt={subcategory.name}
                                className="h-full w-full object-cover"
                              />
                              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/25 to-transparent" />
                            </div>
                          </div>
                        ) : (
                          <div className={`relative flex h-36 items-end overflow-hidden rounded-2xl bg-gradient-to-br ${CARD_GRADIENTS[index % CARD_GRADIENTS.length]} p-4 text-white`}>
                            <Tag className="absolute left-3 top-3 h-5 w-5 text-white/90" />
                            <p className="text-2xl font-black tracking-tight">{subcategory.name}</p>
                          </div>
                        )}

                        <div className="flex flex-1 flex-col px-1 pb-1 pt-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h3 className="text-base font-semibold text-slate-900">{subcategory.name}</h3>
                              <p className="mt-1 text-sm text-slate-500">
                                {subcategory.minPriceLabel
                                  ? `A partir de ${subcategory.minPriceLabel}`
                                  : `${subcategory.serviceCount} service(s) disponible(s)`}
                              </p>
                            </div>
                            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition group-hover:border-blue-200 group-hover:text-blue-600">
                              <ArrowRight className="h-4 w-4" />
                            </span>
                          </div>

                          <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-400">
                            <span>{subcategory.serviceCount} service(s)</span>
                            <span>Voir les services</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )
        ) : visibleServices.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center">
            <p className="text-base font-semibold text-slate-700">Aucun service trouve</p>
            <p className="mt-2 text-sm text-slate-500">
              Essayez un autre mot-cle ou revenez aux sous-categories.
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <>
            <div className="grid grid-cols-2 gap-4 md:hidden">
              {visibleServices.map((service) => {
                const presentation = getCategoryPresentation(selectedCategory?.name || service.categoryLabel);
                const AccentIcon = presentation.icon;
                const mobileServiceImageKey = buildImageStateKey('topup-mobile-service', service.id, service.imageUrl || '');

                return (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => handleOpenService(service)}
                    className={`group relative min-h-[190px] overflow-hidden rounded-[26px] border p-3 text-left shadow-[0_12px_30px_rgba(15,23,42,0.08)] transition active:scale-[0.99] ${presentation.cardClass}`}
                  >
                    <div className={`absolute -right-6 top-10 h-20 w-20 rounded-full blur-2xl ${presentation.orbClass}`} />
                    <div className={`absolute -bottom-6 -left-3 h-16 w-16 rounded-full blur-2xl ${presentation.orbClass}`} />

                    <div className="relative z-10 flex items-start gap-3">
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${presentation.accentClass}`}>
                        <AccentIcon className="h-5 w-5" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="line-clamp-2 text-[1.05rem] font-semibold leading-5 text-slate-800">
                          {service.name}
                        </h3>
                      </div>
                    </div>

                    <div className={`relative z-10 mt-4 h-[108px] overflow-hidden rounded-[22px] bg-gradient-to-br ${presentation.imageShellClass}`}>
                      {service.imageUrl && !imageLoadErrors[mobileServiceImageKey] ? (
                        <img
                          src={service.imageUrl}
                          alt={service.name}
                          className="h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
                          onError={() => setImageLoadErrors((current) => ({ ...current, [mobileServiceImageKey]: true }))}
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Tag className="h-10 w-10 text-slate-300" />
                        </div>
                      )}

                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white/60 to-transparent" />
                    </div>

                    <div className="relative z-10 mt-3 flex items-center justify-between">
                      <span className="text-[11px] font-medium text-slate-500">
                        {buildServiceMeta(service)}
                      </span>
                    </div>

                    <div className={`absolute bottom-4 right-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur ${presentation.actionClass}`}>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-4">
              {visibleServices.map((service, index) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => handleOpenService(service)}
                  className="group flex h-full flex-col rounded-[26px] border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg"
                >
                  {renderServiceVisual(service, index)}

                  <div className="flex flex-1 flex-col px-1 pb-1 pt-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">{service.name}</h3>
                        <p className="mt-1 text-sm text-slate-500">{buildServiceSubtitle(service)}</p>
                      </div>
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition group-hover:border-blue-200 group-hover:text-blue-600">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-400">
                      <span>{service.categoryLabel}</span>
                      <span>{service.offerCount || 0} offre(s)</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            {visibleServices.map((service, index) => {
              const Icon = SERVICE_ICON_MAP[service.categoryKey] || Gamepad2;
              const listServiceImageKey = buildImageStateKey('topup-list-service', service.id, service.imageUrl || '');

              return (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => handleOpenService(service)}
                  className="group flex w-full items-center gap-4 rounded-[24px] border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-200 hover:shadow-md"
                >
                  <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                    {service.imageUrl && !imageLoadErrors[listServiceImageKey] ? (
                      <img
                        src={service.imageUrl}
                        alt={service.name}
                        className="h-full w-full object-cover"
                        onError={() => setImageLoadErrors((current) => ({ ...current, [listServiceImageKey]: true }))}
                      />
                    ) : (
                      <div className={`flex h-full w-full items-center justify-center rounded-2xl bg-gradient-to-br ${CARD_GRADIENTS[index % CARD_GRADIENTS.length]} text-white`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-base font-semibold text-slate-900">{service.name}</h3>
                      {service.popular ? (
                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-600">
                          Populaire
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 truncate text-sm text-slate-500">{buildServiceSubtitle(service)}</p>
                  </div>

                  <span className="text-xs text-slate-400">{service.categoryLabel}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-blue-600" />
                </button>
              );
            })}
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopupHub;
